import { InvalidRoomOutlineError } from "building-types/errors/buildingGenerationError";
import { RoomOutlineProblem } from "building-types/rooms/checkRoomOutline";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallGap } from "building-types/walls/wallGap";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallStrip } from "building-types/walls/wallStrip";
import type { WallMassTable, WallTable } from "building-types/walls/wallTableTypes";
import type { SolidWall, Wall } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeStart } from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2RepairEdgeIndex, polyLine2Copy, polyLine2EdgeLength } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { vec2AngleBetween, vec2Dist, vec2DistSquared, vec2Lerp } from "geom/vec2";
import { getWallPath } from "./wall";
import { wallAngleBendCW, wallCenterLineEnd, wallCenterLineStart, wallHalfEdge, wallOpposite } from "./wall";
import { solidWallSubstrateLayer } from "./wallLayers";
import { WallStripImpl } from "./wallStripImpl";

export class WallIndexImpl implements WallIndex {
  constructor(
    readonly walls: WallTable | WallMassTable,
    readonly halfEdgeToWall: string[],
    readonly roomLevelIndices: Table<number>,
    readonly planarGraph: PolygonPlanarGraph,
  ) {}

  wallParentRoomEdgeIndex(room: Room, wall: Wall) {
    const edges = this.planarGraph.polygonToPlanarGraph[this.roomLevelIndices[room.id]];
    const halfEdge = wallHalfEdge(room, wall);

    const faceHalfEdges = this.planarGraph.planarGraph.faceHalfEdges;

    // find the matching half edge for the given wall
    for (let i = 0, li = edges.length; i < li; i++) {
      const subEdges = edges[i];

      for (let j = 0, lj = subEdges.length; j < lj; j++) {
        const { index, edgeIndex } = subEdges[j];

        if (halfEdge === faceHalfEdges[index][edgeIndex]) {
          return i;
        }
      }
    }

    return -1;
  }

  wallStripsFromRectRoom(room: Room): [WallStrip | null, WallStrip | null, WallStrip | null, WallStrip | null] {
    return [
      this.wallStripFromRoomEdgeIndex(room, 0),
      this.wallStripFromRoomEdgeIndex(room, 1),
      this.wallStripFromRoomEdgeIndex(room, 2),
      this.wallStripFromRoomEdgeIndex(room, 3),
    ];
  }

  wallOppositeRoomEdgeIndex(room: Room, walls: Wall[], wallIndex: number) {
    const oppositeWall = wallOpposite(room, walls, wallIndex);

    if (!oppositeWall) {
      return -1;
    }
    return this.wallParentRoomEdgeIndex(room, oppositeWall);
  }

  wallFromRoomEdge(room: Room, roomEdgeIndex: number, subEdgeIndex: number = 0): Wall | null {
    const edgeRefs = this.planarGraph.polygonToPlanarGraph[this.roomLevelIndices[room.id]];
    if (!edgeRefs) {
      return null;
    }
    const roomEdgeRefs = edgeRefs[roomEdgeIndex];
    if (!roomEdgeRefs) {
      return null;
    }
    const subEdgeRef = roomEdgeRefs[subEdgeIndex];
    if (subEdgeRef === undefined) {
      return null;
    }

    const { index, edgeIndex } = subEdgeRef;
    const halfEdge = this.planarGraph.planarGraph.faceHalfEdges[index][edgeIndex];

    return this.walls[this.halfEdgeToWall[halfEdge]];
  }

  wallsInRoom(room: Room | undefined): Wall[] {
    /**
     * WALLS V2 TODO
     *
     * @NOTE - this is a temporary fix to support walls v2,
     * since some roomIds on wall paths may not resolve to rooms in the building.
     *
     * @TODO @jaggyluis - need a cleaner solution for this. consider guarding the room check at
     * all the call sites for this function
     */
    if (!room) {
      return [];
    }

    const edgeRef = this.planarGraph.polygonToPlanarGraph[this.roomLevelIndices[room.id]]?.[0]?.[0];
    if (!edgeRef) {
      return [];
    }

    const walls: Wall[] = [];
    for (const halfEdge of this.planarGraph.planarGraph.faceHalfEdges[edgeRef.index]) {
      walls.push(this.walls[this.halfEdgeToWall[halfEdge]]);
    }
    return walls;
  }

  solidWallStripsInRoomSet(
    roomSet: RoomSet,
    rooms: Table<Room>,
    levelRooms: Room[],
    linearize: boolean = false,
    includeWallOffset: boolean = false,
  ): WallStrip[] {
    const wallStrips: WallStrip[] = [];
    const groupRooms = roomSet;
    const roomSetIds = new Set(groupRooms.map((room) => room.id));

    let startRoomId = "";
    let startRoomWallId = -1;

    // look for a room with a solid wall
    for (let i = 0, l = groupRooms.length; i < l; i++) {
      const roomId = groupRooms[i].id;
      const walls = this.wallsInRoom(rooms[roomId]);

      for (let j = 0, lj = walls.length; j < lj; j++) {
        if (walls[j].wallType !== WallType.VOID) {
          startRoomId = roomId;
          startRoomWallId = j;
          break;
        }
      }

      if (startRoomId) {
        break;
      }
    }

    // if we have found a valid start room
    if (startRoomId) {
      let currentRoomId = startRoomId;
      let currentRoomWallId = startRoomWallId;

      let looped = false;
      const loopedVoidWalls: { [key: string]: boolean } = {};
      const loopedSolidWalls: { [key: string]: boolean } = {};

      let lastRoomId: string | undefined = undefined;
      let lastRoomWallId: number | undefined = undefined;

      do {
        const room = rooms[currentRoomId];
        const walls = this.wallsInRoom(room);

        let currentStripStart = currentRoomWallId;
        let currentStripCount = 0;

        for (let i = 0, l = walls.length; i < l; i++) {
          const wall = walls[currentRoomWallId];

          if (wall.wallType !== WallType.VOID) {
            // if it is a solid wall add it to the current strip
            currentStripCount++;

            currentRoomWallId = (currentRoomWallId + 1) % l;

            // break if we have closed the loop
            if (currentRoomId === startRoomId && currentRoomWallId === startRoomWallId) {
              break;
            }

            // break out of an infinite loop - something went wrong
            // in the planar graph generation and should be handled elsewhere
            // (keep in mind that a wall can be visited from different rooms,
            // so we need to keep track of that, adding the room id to the key)
            const solidWallKey = `${currentRoomId}_${wall.id}`;
            if (loopedSolidWalls[solidWallKey]) {
              console.warn("Detected planarGraph wall loop");
              looped = true;
              break;
            }

            loopedSolidWalls[solidWallKey] = true;
          } else {
            // push the current strip
            if (currentStripCount > 0) {
              wallStrips.push(new WallStripImpl(room, walls, currentStripStart, currentStripCount));
              currentStripCount = 0;
            }

            // if it is a void wall, travel to the adjacent room
            const adjHalfEdgeIndex = planarGraphAdjacentHalfEdge(wallHalfEdge(room, wall));
            const adjFaceIndex = this.planarGraph.planarGraph.halfEdgeParentFace[adjHalfEdgeIndex];
            const polygonMap = this.planarGraph.planarGraphToPolygon[adjFaceIndex];
            const adjFaceHalfEdges = this.planarGraph.planarGraph.faceHalfEdges[adjFaceIndex];

            const adjRoomId = polygonMap.length !== 0 && polygonMap[0] ? levelRooms[polygonMap[0].index].id : undefined;
            const adjWallIndex = adjFaceHalfEdges.indexOf(adjHalfEdgeIndex);
            const adjWallCount = adjFaceHalfEdges.length;

            // Verify that the adjacent room is valid and is part of the input roomSet using Set lookup
            const isAdjRoomInSet = adjRoomId && roomSetIds.has(adjRoomId);

            if (isAdjRoomInSet && adjRoomId !== currentRoomId && adjWallIndex >= 0) {
              // if the adjacent room is a valid room, move to it and take the next wall
              currentRoomId = adjRoomId;
              currentRoomWallId = (adjWallIndex + 1) % adjWallCount;
              break;
            } else {
              // if it's not a valid room, take the next wall
              currentRoomWallId = (currentRoomWallId + 1) % l;
              currentStripStart = currentRoomWallId;

              // break if we have closed the loop
              if (currentRoomId === startRoomId && currentRoomWallId === startRoomWallId) {
                break;
              }

              // break out of an infinite loop - something went wrong
              // in the planar graph generation and should be handled elsewhere
              if (loopedVoidWalls[wall.id]) {
                console.warn("Detected planarGraph VOID wallloop");
                looped = true;
                break;
              }

              loopedVoidWalls[wall.id] = true;
            }
          }
        }

        // push the current strip
        if (currentStripCount > 0) {
          wallStrips.push(new WallStripImpl(room, walls, currentStripStart, currentStripCount));
        }

        if (currentRoomId === lastRoomId && lastRoomWallId === currentRoomWallId) {
          //edge travel is stuck, preventing an infinite loop
          throw new InvalidRoomOutlineError(
            { type: "ROOM", id: currentRoomId },
            RoomOutlineProblem.LIKELY_SELF_INTERSECTS,
          );
        }

        lastRoomId = currentRoomId;
        lastRoomWallId = currentRoomWallId;
      } while (!looped && (currentRoomId !== startRoomId || currentRoomWallId !== startRoomWallId));
    }

    if (wallStrips.length > 1) {
      const startStrip = wallStrips[0];
      const endStrip = wallStrips[wallStrips.length - 1];

      if (startStrip !== endStrip && startStrip.room.id === endStrip.room.id) {
        const walls = this.wallsInRoom(startStrip.room);
        const [firstX, firstY] = wallCenterLineStart(startStrip.room, walls[startStrip.startIndex]);
        const [lastX, lastY] = wallCenterLineEnd(endStrip.room, walls[startStrip.startIndex + startStrip.count - 1]);

        if (vec2DistSquared(firstX, firstY, lastX, lastY) < DIST_EPSILON_SQUARED) {
          wallStrips[0] = new WallStripImpl(
            startStrip.room,
            walls,
            startStrip.startIndex,
            startStrip.count + endStrip.count,
          );
          wallStrips.pop();
        }
      }
    }

    return linearize ? WallStripImpl.allToStraightStrips(wallStrips, includeWallOffset) : wallStrips;
  }

  wallStripsByPredicate(
    room: Room,
    predicate: (wall: Wall, index: number) => boolean,
    linearize: boolean = false,
    includeWallOffset: boolean = false,
  ): WallStrip[] {
    const walls = this.wallsInRoom(room);
    const strips: WallStrip[] = [];

    let curStrip;

    for (let i = 0, l = walls.length; i < l; i++) {
      const wall = walls[i];
      const predicateVal = predicate(wall, i);

      if (predicateVal) {
        // the strip may not be initialized yet
        if (!curStrip) {
          curStrip = new WallStripImpl(room, walls, i, 0);
        }

        // extend the strip by 1 wall
        curStrip.count++;

        // if we are at the end of the wall set,
        // we terminate the strip
        if (i === l - 1) {
          strips.push(curStrip);
        }
      }

      if (!predicateVal && curStrip && curStrip.count > 0) {
        // we have a non-empty strip, but the predicate is no longer
        // true. add the current strip to the result list.
        strips.push(curStrip);

        // reset the current strip
        curStrip = null;
      }
    }

    // if the last and first strip are connected, turn them into a single strip
    if (strips.length > 1) {
      const startStrip = strips[0];
      const endStrip = strips[strips.length - 1];

      if (startStrip !== endStrip) {
        const [firstX, firstY] = wallCenterLineStart(room, walls[startStrip.startIndex]);
        const [lastX, lastY] = wallCenterLineEnd(
          room,
          walls[(endStrip.startIndex + endStrip.count - 1) % walls.length],
        );

        if (vec2Dist(firstX, firstY, lastX, lastY) < DIST_EPSILON) {
          strips[0] = new WallStripImpl(room, walls, endStrip.startIndex, startStrip.count + endStrip.count);
          strips.pop();
        }
      }
    } else if (strips.length === 1 && strips[0].count === walls.length) {
      // case was previously unhandled where a single room strip starts midway through a collinear room edge and is continuous
      //    we need to reset the beginning of the strip to the wallIndex that starts the edge
      const onlyStrip = strips[0];
      const firstWall = walls[onlyStrip.startIndex];
      const lastWallIndex = walls.length - 1;

      let wallIndexOfLastTangentWall: number | undefined = undefined;
      for (let i = lastWallIndex; i > 0; i--) {
        const potentialNewFirstWall = walls[i];

        if (Math.abs(wallAngleBendCW(room, firstWall, potentialNewFirstWall)) < ANGLE_EPSILON) {
          const [firstX, firstY] = wallCenterLineStart(room, firstWall);
          const [lastX, lastY] = wallCenterLineEnd(room, potentialNewFirstWall);

          if (i === walls.length - 1) {
            if (vec2Dist(firstX, firstY, lastX, lastY) < DIST_EPSILON) {
              wallIndexOfLastTangentWall = walls.length - 1;
            } else {
              break;
            }
          } else {
            wallIndexOfLastTangentWall = i;
          }
        } else {
          break;
        }
      }

      if (wallIndexOfLastTangentWall !== undefined) {
        strips[0] = new WallStripImpl(room, walls, wallIndexOfLastTangentWall, walls.length);
      }
    }

    return linearize ? WallStripImpl.allToStraightStrips(strips, includeWallOffset) : strips;
  }

  wallStripFromRoomEdgeIndex(room: Room, roomEdgeIndex: number): WallStrip | null {
    roomEdgeIndex = polyLine2RepairEdgeIndex(room.outline, roomEdgeIndex);

    const roomWalls: Wall[] = this.wallsInRoom(room);

    const subEdges = this.planarGraph.polygonToPlanarGraph[this.roomLevelIndices[room.id]][roomEdgeIndex];
    if (!subEdges || subEdges.length === 0) {
      return null;
    }

    const { index, edgeIndex } = subEdges[0];

    const startHalfEdge = this.planarGraph.planarGraph.faceHalfEdges[index][edgeIndex];
    const roomWallsIndex = roomWalls.findIndex((wall) => wallHalfEdge(room, wall) === startHalfEdge);
    if (roomWallsIndex < 0) {
      console.assert(false, "invalid room walls index");
      return null;
    }

    return new WallStripImpl(room, roomWalls, roomWallsIndex, subEdges.length);
  }

  parentWallStrip(room: Room, wall: Wall): WallStrip | null {
    return this.wallStripFromRoomEdgeIndex(room, this.wallParentRoomEdgeIndex(room, wall));
  }

  mergeCollinearWallStrips(wallStrips: WallStrip[], useDoors: boolean, useWindows: boolean): PolyLine2[] {
    const segments: PolyLine2[] = [];

    function wallStripsCollinearAndContiguous(prevStrip: PolyLine2, nextStrip: PolyLine2): boolean {
      return (
        vec2DistSquared(prevStrip[2], prevStrip[3], nextStrip[0], nextStrip[1]) < DIST_EPSILON_SQUARED &&
        Math.abs(
          vec2AngleBetween(
            prevStrip[2] - prevStrip[0],
            prevStrip[3] - prevStrip[1],
            nextStrip[2] - nextStrip[0],
            nextStrip[3] - nextStrip[1],
          ),
        ) < ANGLE_EPSILON
      );
    }

    let currentSegment: PolyLine2 | null = null;

    for (let i = 0, l = wallStrips.length; i < l; i++) {
      const strip = wallStrips[i];
      if (strip.count > 0) {
        const pl = strip.baseline(true);
        // console.assert(pl.length === 4, "WallIndex.mergeCollinearWallStrips assumes the strip is straight");

        if (currentSegment && !wallStripsCollinearAndContiguous(currentSegment, pl)) {
          segments.push(currentSegment);
          currentSegment = null;
        }

        if (!currentSegment) {
          currentSegment = polyLine2Copy(pl);
        }

        const doorParams: WallGap[] = [];

        if (useDoors) {
          doorParams.push(...strip.doorGaps());
        }

        if (useWindows) {
          doorParams.push(...strip.windowGaps());
        }

        doorParams.sort((a, b) => a.center - b.center);

        for (let j = 0, lj = doorParams.length; j < lj; j++) {
          const doorParam = doorParams[j];
          if (doorParam !== undefined) {
            [currentSegment[2], currentSegment[3]] = vec2Lerp(pl[0], pl[1], pl[2], pl[3], doorParam.start);

            segments.push(currentSegment);

            currentSegment = polyLine2Copy(pl);
            [currentSegment[0], currentSegment[1]] = vec2Lerp(pl[0], pl[1], pl[2], pl[3], doorParam.end);
          }
        }

        currentSegment[2] = pl[2];
        currentSegment[3] = pl[3];
      }
    }

    if (currentSegment) {
      if (segments.length > 0 && wallStripsCollinearAndContiguous(currentSegment, segments[0])) {
        const firstSegment = segments[0];
        firstSegment[0] = currentSegment[0];
        firstSegment[1] = currentSegment[1];
      } else {
        segments.push(currentSegment);
      }
    }

    return segments;
  }

  mergeCollinearWallStripsReturnHalfEdges(wallStrips: WallStrip[]): {
    segments: PolyLine2[];
    segmentHalfEdges: number[][];
  } {
    function wallStripsCollinearAndContiguous(prevStrip: PolyLine2, nextStrip: PolyLine2): boolean {
      return (
        vec2DistSquared(prevStrip[2], prevStrip[3], nextStrip[0], nextStrip[1]) < DIST_EPSILON_SQUARED &&
        Math.abs(
          vec2AngleBetween(
            prevStrip[2] - prevStrip[0],
            prevStrip[3] - prevStrip[1],
            nextStrip[2] - nextStrip[0],
            nextStrip[3] - nextStrip[1],
          ),
        ) < ANGLE_EPSILON
      );
    }

    const segments: PolyLine2[] = [];
    const segmentHalfEdges: number[][] = [];

    let currentSegment: PolyLine2 | null = null;
    let currentSegmentHalfEdges: number[] = [];

    for (let i = 0, l = wallStrips.length; i < l; i++) {
      const strip = wallStrips[i];
      if (strip.count > 0) {
        const { pl, edgeToWallId } = strip.baselineWithEdgeInfo(true, false, true);
        if (pl.length === 0) {
          continue;
        }

        if (currentSegment && !wallStripsCollinearAndContiguous(currentSegment, pl)) {
          segments.push(currentSegment);
          segmentHalfEdges.push(currentSegmentHalfEdges);

          currentSegment = null;
          currentSegmentHalfEdges = [];
        }

        if (!currentSegment) {
          currentSegment = polyLine2Copy(pl);
        }

        for (const wallId of edgeToWallId) {
          const wall = this.walls[wallId];
          if (!wall) {
            continue;
          }
          const path = getWallPath(wall);
          if (strip.room === path.leftRoom) {
            currentSegmentHalfEdges.push(path.halfEdgeIndex);
          } else {
            currentSegmentHalfEdges.push(planarGraphAdjacentHalfEdge(path.halfEdgeIndex));
          }
        }

        currentSegment[2] = pl[2];
        currentSegment[3] = pl[3];
      }
    }

    if (currentSegment) {
      if (segments.length > 0 && wallStripsCollinearAndContiguous(currentSegment, segments[0])) {
        const firstSegment = segments[0];
        firstSegment[0] = currentSegment[0];
        firstSegment[1] = currentSegment[1];
      } else {
        segments.push(currentSegment);
        segmentHalfEdges.push(currentSegmentHalfEdges);
      }
    }

    return {
      segments,
      segmentHalfEdges,
    };
  }

  longestSolidWallStrips(solidStrips: WallStrip[], useDoors: boolean = true, useWindows: boolean = false): PolyLine2[] {
    const wallStrips = this.mergeCollinearWallStrips(solidStrips, useDoors, useWindows);

    wallStrips.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

    return wallStrips;
  }

  longestSolidWallStripsInRoom(room: Room, useDoors: boolean = true, useWindows: boolean = false): PolyLine2[] {
    const solidStrips = this.wallStripsByPredicate(room, (wall) => wall.wallType !== WallType.VOID, true);

    return this.longestSolidWallStrips(solidStrips, useDoors, useWindows);
  }

  longestSolidWallStripsInRoomSet(
    roomSet: RoomSet,
    rooms: Table<Room>,
    levelRooms: Room[],
    useDoors: boolean = true,
    useWindows: boolean = false,
  ): PolyLine2[] {
    const solidStrips = this.solidWallStripsInRoomSet(roomSet, rooms, levelRooms, true);

    return this.longestSolidWallStrips(solidStrips, useDoors, useWindows);
  }

  halfEdgeStripToPolyLine2ByWalls(
    halfEdgeLoop: ArrayLike<number>,
    getLeftEdge: (wall: SolidWall) => boolean,
  ): PolyLine2 {
    const plen = halfEdgeLoop.length * 2 + 2;
    const pl = polyLine2New(plen);

    for (let i = 0, l = halfEdgeLoop.length; i < l; i++) {
      const wallId = this.halfEdgeToWall[halfEdgeLoop[i]];
      const wall = this.walls[wallId];
      const [edgeStartX, edgeStartY] = planarGraphHalfEdgeStart(this.planarGraph.planarGraph, halfEdgeLoop[i]);
      const i2 = 2 * i;
      let path: PolyLine2;
      if (wall.wallType === WallType.SOLID) {
        const useLeft = getLeftEdge(wall);
        const { leftEdge, rightEdge } = solidWallSubstrateLayer(wall);
        path = useLeft ? leftEdge : rightEdge;
      } else {
        path = polyLine2New([wall.path.startX, wall.path.startY, wall.path.endX, wall.path.endY]);
      }
      const startIndex =
        vec2DistSquared(edgeStartX, edgeStartY, path[0], path[1]) <=
        vec2DistSquared(edgeStartX, edgeStartY, path[2], path[3])
          ? 0
          : 2;
      pl[i2] = path[startIndex];
      pl[i2 + 1] = path[startIndex + 1];
      if (i === l - 1) {
        const endIndex = startIndex === 0 ? 2 : 0;
        pl[i2 + 2] = path[endIndex];
        pl[i2 + 3] = path[endIndex + 1];
      }
    }
    return pl;
  }
}
